import type { BoardListFilter, BoardSummary, SpaceSummary } from '@doriavboard/shared';
import { FileUp, FolderOpen, FolderPlus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { errorMessage } from '../api/client';
import { boardsApi, spacesApi } from '../api/endpoints';
import { AccountDialog } from '../components/AccountDialog';
import { BoardGrid } from '../components/BoardGrid';
import { Logo } from '../components/Logo';
import { NewBoardStrip, templateTitle } from '../components/NewBoardStrip';
import { UserMenu } from '../components/UserMenu';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/cn';
import { othersInSpace } from '../lib/users';
import { promptDialog } from '../stores/dialogs';
import { useSession } from '../stores/session';
import { toast } from '../stores/toasts';

const TABS: { id: BoardListFilter; label: string }[] = [
  { id: 'recent', label: 'Recent' },
  { id: 'owned', label: 'My boards' },
  { id: 'shared', label: 'Shared with me' },
];

const EMPTY: Record<BoardListFilter, string> = {
  recent: 'Boards you open will show up here.',
  owned: 'You haven’t created any boards yet. Pick a template above to start.',
  shared: 'When someone shares a board with you, it appears here.',
};

/** Asks for a name and creates a space, resolving to it (or null if cancelled). */
export async function createSpaceDialog(): Promise<SpaceSummary | null> {
  const name = await promptDialog({
    title: 'New space',
    label: 'Name',
    placeholder: 'For example, a student’s or a class’s name',
    confirmLabel: 'Create space',
  });
  if (!name) return null;
  try {
    return (await spacesApi.create(name)).space;
  } catch (err) {
    toast(errorMessage(err), 'error');
    return null;
  }
}

function SpaceCard({ space }: { space: SpaceSummary }) {
  const me = useSession((s) => s.user);
  const others = othersInSpace(space, me?.id);
  return (
    <Link
      to={`/s/${space.id}`}
      className="focus-ring group flex w-60 shrink-0 items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-slate-300 hover:shadow-float dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
        <FolderOpen className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium" title={space.name}>
          {space.name}
        </span>
        <span className="block truncate text-xs text-slate-500">
          {space.boardCount === 1 ? '1 board' : `${space.boardCount} boards`}
          {space.role !== 'owner' && ` · ${space.owner.name}’s`}
        </span>
      </span>
      {others.length > 0 && (
        <span className="flex -space-x-1.5">
          {others.slice(0, 3).map((person) => (
            <Avatar key={person.id} name={person.name} color={person.color} size="xs" ring />
          ))}
        </span>
      )}
    </Link>
  );
}

function SpacesSection({ spaces, query }: { spaces: SpaceSummary[] | null; query: string }) {
  const navigate = useNavigate();
  const q = query.trim().toLowerCase();
  const visible = q ? spaces?.filter((s) => s.name.toLowerCase().includes(q)) : spaces;
  if (!visible || (q && visible.length === 0)) return null;

  async function create() {
    const space = await createSpaceDialog();
    if (space) navigate(`/s/${space.id}`);
  }

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h2 className="text-sm font-medium text-slate-600 dark:text-slate-300">Spaces</h2>
        <Button variant="ghost" size="sm" onClick={create}>
          <FolderPlus className="size-4" />
          New space
        </Button>
      </div>
      {visible.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500 dark:border-slate-700">
          Keep the boards you share with the same people together, such as one space per student or
          class. Everyone in a space can open all of its boards.
        </p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {visible.map((space) => (
            <SpaceCard key={space.id} space={space} />
          ))}
        </div>
      )}
    </section>
  );
}

/** Reminds guests that their boards only live in this browser's session. */
function GuestBanner() {
  const isGuest = useSession((s) => s.user?.isGuest ?? false);
  const [open, setOpen] = useState(false);
  if (!isGuest) return null;
  return (
    <div className="border-b border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2.5 text-sm sm:px-6">
        <p className="flex-1 text-amber-900 dark:text-amber-200">
          You’re using DoriavBoard as a guest. Save your account to sign in from other devices and
          keep your boards.
        </p>
        <Button size="sm" onClick={() => setOpen(true)}>
          Save your account
        </Button>
      </div>
      <AccountDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<BoardListFilter>('recent');
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [spaces, setSpaces] = useState<SpaceSummary[] | null>(null);
  const [query, setQuery] = useState('');
  const importInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async (filter: BoardListFilter) => {
    setBoards(null);
    try {
      const { boards: list } = await boardsApi.list(filter);
      setBoards(list);
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBoards([]);
    }
  }, []);

  useEffect(() => {
    void load(tab);
  }, [tab, load]);

  useEffect(() => {
    document.title = 'Boards · DoriavBoard';
    spacesApi
      .list()
      .then(({ spaces: list }) => setSpaces(list))
      .catch((err) => toast(errorMessage(err), 'error'));
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!boards || !q) return boards;
    return boards.filter(
      (b) =>
        b.title.toLowerCase().includes(q) ||
        b.owner.name.toLowerCase().includes(q) ||
        b.space?.name.toLowerCase().includes(q),
    );
  }, [boards, query]);

  async function importFile(file: File) {
    try {
      const { board } = await boardsApi.import(file);
      toast(`Imported “${board.title}”`, 'success');
      navigate(`/b/${board.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Logo />
          <div className="relative mx-auto hidden w-full max-w-md sm:block">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search boards and spaces"
              aria-label="Search boards and spaces"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 w-full rounded-full bg-slate-100 pr-4 pl-9 text-sm outline-none ring-brand-500/30 transition placeholder:text-slate-400 focus:bg-white focus:ring-3 dark:bg-slate-900 dark:focus:bg-slate-900"
            />
          </div>
          <div className="ml-auto flex items-center gap-2 sm:ml-0">
            <Button variant="ghost" size="sm" onClick={() => importInput.current?.click()}>
              <FileUp className="size-4" />
              <span className="hidden md:inline">Import</span>
            </Button>
            <input
              ref={importInput}
              type="file"
              accept=".dvb"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void importFile(file);
              }}
            />
            <UserMenu />
          </div>
        </div>
      </header>
      <GuestBanner />

      <NewBoardStrip
        heading="Start a new board"
        create={async (template) =>
          (await boardsApi.create({ template, title: templateTitle(template) })).board.id
        }
      />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <SpacesSection spaces={spaces} query={query} />

        <div role="tablist" aria-label="Board lists" className="mb-5 flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'focus-ring rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:bg-slate-200/70 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <BoardGrid
          boards={visible}
          empty={query ? `No boards match “${query}”.` : EMPTY[tab]}
          onUpdate={setBoards}
        />
      </main>
    </div>
  );
}
